import { DuckActions, StandardThunk } from 'main/store';
import { errorToString } from 'util/backendapi/error';
import { getApi } from 'util/backendapi/fetch';
import { StoredVerticalProfilePlotWithArea } from 'ducks/stored-plot/detail';
import { VerticalProfilePlotItemReadings } from 'util/backendapi/types/Model';
import { FullState } from 'main/reducers';

/***
 * Duck for rendering a vertical profile plot
 */
export const ActionTypes = {
  FETCH_PLOTTING_DATA_START:
    'dms/storedPlot/verticalProfilePlot/FETCH_PLOTTING_DATA_START',
  FETCH_PLOTTING_ITEM_DATA_RESPONSE:
    'dms/storedPlot/verticalProfilePlot/FETCH_PLOTTING_ITEM_DATA_RESPONSE',
  FETCH_PLOTTING_DATA_ERROR:
    'dms/storedPlot/verticalProfilePlot/FETCH_PLOTTING_DATA_ERROR',
  UNMOUNT: 'dms/storedPlot/verticalProfilePlot/UNMOUNT',
} as const;

export const ActionCreators = {
  FETCH_PLOTTING_DATA_START: (storedPlotId: number) => ({
    type: ActionTypes.FETCH_PLOTTING_DATA_START,
    storedPlotId,
  }),
  FETCH_PLOTTING_ITEM_DATA_RESPONSE: (
    storedPlotId: number,
    storedVerticalProfilePlotItemId: number,
    plotReadings: VerticalProfilePlotItemReadings
  ) => ({
    type: ActionTypes.FETCH_PLOTTING_ITEM_DATA_RESPONSE,
    storedPlotId,
    storedVerticalProfilePlotItemId,
    payload: {
      plotReadings,
    },
  }),
  FETCH_PLOTTING_DATA_ERROR: (storedPlotId: number, errorMessage: string) => ({
    type: ActionTypes.FETCH_PLOTTING_DATA_ERROR,
    storedPlotId,
    error: true,
    payload: errorMessage,
  }),
  UNMOUNT: () => ({ type: ActionTypes.UNMOUNT }),
};

export type VerticalProfilePlotAction = DuckActions<
  typeof ActionTypes,
  typeof ActionCreators
>;

export interface SingleVerticalProfilePlotState {
  storedPlotId: number | null;
  isLoading: boolean;
  errorMessage: string | null;
  plotItemReadings: {
    [key: number]: VerticalProfilePlotItemReadings;
  };
}

type VerticalProfilePlotState = {
  [K in number]?: SingleVerticalProfilePlotState;
};

function initialState(): VerticalProfilePlotState {
  return {};
}

export function verticalProfilePlotReducer(
  state = initialState(),
  action: VerticalProfilePlotAction
): VerticalProfilePlotState {
  switch (action.type) {
    case ActionTypes.FETCH_PLOTTING_DATA_START:
      return {
        ...state,
        [action.storedPlotId]: {
          storedPlotId: action.storedPlotId,
          isLoading: true,
          errorMessage: null,
          plotItemReadings: {},
        },
      };
    case ActionTypes.FETCH_PLOTTING_ITEM_DATA_RESPONSE: {
      const plotState = state[action.storedPlotId];
      if (plotState) {
        return {
          ...state,
          [action.storedPlotId]: {
            ...plotState,
            isLoading: false,
            errorMessage: null,
            plotItemReadings: {
              ...plotState.plotItemReadings,
              [action.storedVerticalProfilePlotItemId]:
                action.payload.plotReadings,
            },
          },
        };
      } else {
        return state;
      }
    }
    case ActionTypes.FETCH_PLOTTING_DATA_ERROR: {
      const plotState = state[action.storedPlotId];
      if (plotState) {
        return {
          ...state,
          [action.storedPlotId]: {
            ...plotState,
            isLoading: false,
            errorMessage: action.payload,
          },
        };
      } else {
        return state;
      }
    }
    case ActionTypes.UNMOUNT:
      return initialState();
    default:
      return state;
  }
}

export function selectVerticalProfilePlotData(
  state: FullState,
  storedPlotId: number
) {
  return state.plot.verticalProfile[storedPlotId];
}

export const unmountVerticalProfilePlot = ActionCreators.UNMOUNT;

export function fetchVerticalProfileStoredPlotData(
  storedPlot: StoredVerticalProfilePlotWithArea
): StandardThunk {
  return async function (dispatch) {
    const storedPlotId = storedPlot.id;

    dispatch(ActionCreators.FETCH_PLOTTING_DATA_START(storedPlotId));
    try {
      for (
        let i = 0;
        i < storedPlot.stored_vertical_profile_plot_items.length;
        i++
      ) {
        const storedVerticalProfilePlotItemId =
          storedPlot.stored_vertical_profile_plot_items[i].id;

        const plotItemReadings = await getApi(
          '/readings/vertical-profile-plot/',
          {
            stored_vertical_profile_plot_item: storedVerticalProfilePlotItemId,
          }
        );

        dispatch(
          ActionCreators.FETCH_PLOTTING_ITEM_DATA_RESPONSE(
            storedPlotId,
            storedVerticalProfilePlotItemId,
            plotItemReadings
          )
        );
      }
    } catch (e) {
      dispatch(
        ActionCreators.FETCH_PLOTTING_DATA_ERROR(storedPlotId, errorToString(e))
      );
    }
  };
}
